import { REGIONS, coast, BRIDGES, contains } from "./regions";
import { ellipse, shape, drawObject } from "./living-art";
import { ECOLOGY } from "./ecology";
import type { Joint } from "./motion";
import type { Vec2 } from "./types";
import { inkAsset, inkPart, inkRevision, loadInkAssets } from "./ink-assets";
const noise = (n: number) => {
  const v = Math.sin(n * 127.1 + 31.7) * 43758.5453;
  return v - Math.floor(v);
};
/** Static topography is cached once per palette, while wind, water and fireflies are layered live. */
export class Landscape {
  private cache = new Map<string, HTMLCanvasElement>();
  private revision = -1;
  private texture: HTMLImageElement;
  constructor() {
    loadInkAssets();
    this.texture = new Image();
    this.texture.onload = () => this.cache.clear();
    this.texture.src = "/art/meadow-v6.png";
  }
  draw(
    ctx: CanvasRenderingContext2D,
    time: number,
    camera: Vec2,
    theme = "garden",
  ) {
    if (this.revision !== inkRevision) {
      this.cache.clear();
      this.revision = inkRevision;
    }
    for (const r of REGIONS) {
      if (Math.abs(r.x - camera.x) > 1600) continue;
      const key = theme + r.id;
      let canvas = this.cache.get(key);
      if (!canvas) {
        canvas = document.createElement("canvas");
        canvas.width = 1160;
        canvas.height = 1100;
        const c = canvas.getContext("2d")!;
        c.translate(580 - r.x, 90 - r.y + r.ry);
        this.paintIsland(c, r, theme);
        this.cache.set(key, canvas);
      }
      ctx.drawImage(canvas, r.x - 580, r.y - r.ry - 90);
    }
    for (const b of BRIDGES) {
      const { x, y, width, height } = b;
      const bridge = inkAsset("bridge");
      if (bridge) {
        for (const side of [-1, 1]) {
          const end = x + side * width * 0.48;
          inkPart(ctx, "v8/shorekit/tool", end + side * 38, y + 22, 34);
          inkPart(ctx, "v8/shorekit/upper", end + side * 39, y + 24, 65);
          inkPart(
            ctx,
            side < 0 ? "v8/shorekit/lower" : "v8/shorekit/thigh",
            end,
            y + 30,
            80,
          );
        }
        ctx.drawImage(
          bridge,
          28,
          260,
          1480,
          451,
          x - width * 0.63,
          y - height * 0.5 - 40,
          width * 1.26,
          height + 62,
        );
        continue;
      }
      ellipse(ctx, x + 8, y + 29, width * 0.64, 31, "#254c494f", false);
      shape(
        ctx,
        [
          [x - width / 2 - 8, y - height / 2 + 15],
          [x + width / 2 + 8, y - height / 2 + 15],
          [x + width / 2 + 8, y + height / 2 + 15],
          [x - width / 2 - 8, y + height / 2 + 15],
        ],
        "#635c42",
      );
      for (let i = 0; i < 12; i++) {
        const px = x - width / 2 + (i * width) / 12;
        shape(
          ctx,
          [
            [px, y - height / 2],
            [px + width / 12 - 2, y - height / 2 - 1],
            [px + width / 12 - 2, y + height / 2],
            [px, y + height / 2 + 2],
          ],
          i % 3 ? "#b8a374" : "#c4b181",
        );
        ctx.strokeStyle = "#6d5c4077";
        ctx.lineWidth = 0.8;
        for (let k = 0; k < 3; k++) {
          ctx.beginPath();
          ctx.moveTo(px + 3 + k * 3, y - height / 2 + 4);
          ctx.bezierCurveTo(
            px + 7 + k * 2,
            y - 13,
            px + 1 + k * 3,
            y + 10,
            px + 4 + k * 3,
            y + height / 2 - 3,
          );
          ctx.stroke();
        }
      }
      for (const side of [-1, 1]) {
        ctx.strokeStyle = "#756547";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x - width / 2, y + (side * height) / 2 - 20);
        ctx.quadraticCurveTo(
          x,
          y + (side * height) / 2 - 5,
          x + width / 2,
          y + (side * height) / 2 - 20,
        );
        ctx.stroke();
        for (const f of [-1, 1]) {
          shape(
            ctx,
            [
              [x + (f * width) / 2 - 4, y + (side * height) / 2],
              [x + (f * width) / 2 - 4, y + (side * height) / 2 - 26],
              [x + (f * width) / 2 + 4, y + (side * height) / 2 - 28],
              [x + (f * width) / 2 + 5, y + (side * height) / 2],
            ],
            "#8d805b",
          );
        }
      }
    }
    // Spatially different stems: bases never move, only control points and leaf tips.
    for (const r of REGIONS)
      for (let i = 0; i < 42; i++) {
        const a = i * 2.399,
          radius = 0.77 + noise(i + r.x) * 0.14,
          x = r.x + Math.cos(a) * r.rx * radius,
          y = r.y + Math.sin(a) * r.ry * radius;
        if (!contains({ x, y }, r, 16)) continue;
        if (
          Math.abs(y - 485) < 65 &&
          (Math.abs(x - 350) < 125 || Math.abs(x - 1250) < 125)
        )
          continue;
        const w = Math.sin(time * 0.9 + x * 0.013 + y * 0.006) * 4;
        ctx.strokeStyle = i % 4 ? "#607b55" : "#d2d4a0";
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.quadraticCurveTo(x + w * 0.4, y - 7, x + w, y - 15);
        ctx.moveTo(x, y - 2);
        ctx.quadraticCurveTo(x - 6 + w * 0.2, y - 6, x - 8 + w * 0.6, y - 10);
        ctx.stroke();
        if (i % 5 === 0) ellipse(ctx, x + w, y - 15, 2.5, 2, "#e5ddac", false);
      }
    for (const r of REGIONS)
      for (let i = 0; i < 16; i++) {
        const a = i * 2.399,
          radius = 0.65 + noise(i + 5) * 0.13,
          x = r.x + Math.cos(a) * r.rx * radius,
          y = r.y + Math.sin(a) * r.ry * radius;
        if (!contains({ x, y }, r, 24)) continue;
        if (Math.abs(y - 485) < 105) continue;
        const ids = Object.entries(ECOLOGY).filter(([, v]) =>
          (v.regions as readonly string[]).includes(r.id),
        );
        const [id] = ids[i % ids.length];
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(0.7, 0.7);
        drawObject(ctx, `decor-${id}`, {
          motion: "wind",
          phase: (time % 4) / 4,
          time: time + x * 0.01,
        });
        ctx.restore();
      }
    for (let i = 0; i < 12; i++) {
      const x = camera.x - 660 + noise(i * 3) * 1320,
        y = 260 + noise(i * 7) * 490;
      ctx.globalAlpha = 0.15 + Math.max(0, Math.sin(time + i)) * 0.35;
      ellipse(
        ctx,
        x + Math.sin(time * 0.3 + i) * 8,
        y + Math.cos(time * 0.4 + i) * 6,
        2,
        2,
        "#fcdf9b",
        false,
      );
    }
    ctx.globalAlpha = 1;
  }
  private paintIsland(
    ctx: CanvasRenderingContext2D,
    r: (typeof REGIONS)[number],
    theme: string,
  ) {
    const pts = coast(r);
    const plate = inkAsset(`island-${r.id}`);
    if (plate) {
      this.paintOrganicIsland(ctx, r, theme);
      return;
    }
    const ember = theme === "ember-quarry",
      forest = theme === "thorn-ruins";
    const cliff = ctx.createLinearGradient(0, r.y, 0, r.y + r.ry + 135);
    cliff.addColorStop(0, "#718779");
    cliff.addColorStop(0.6, "#566f66");
    cliff.addColorStop(1, "#365755");
    shape(
      ctx,
      pts.map(([x, y]) => [x, y + 87] as Joint),
      cliff as unknown as string,
      false,
    );
    for (let i = 1; i < pts.length; i++) {
      const [x, y] = pts[i];
      if (y < r.y) continue;
      shape(
        ctx,
        [
          [x, y],
          [x + 15, y + 55],
          [x - 9, y + 85],
          [x - 22, y + 17],
        ],
        i % 3 ? "#698477" : "#87927a",
        false,
      );
      ctx.strokeStyle = "#b9ba882f";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x, y + 7);
      ctx.lineTo(x - 7, y + 64);
      ctx.stroke();
    }
    const grad = ctx.createLinearGradient(
      r.x - r.rx,
      r.y - r.ry,
      r.x + r.rx,
      r.y + r.ry,
    );
    grad.addColorStop(
      0,
      ember
        ? "#bdac7b"
        : forest
          ? "#849d78"
          : r.id === "quarry"
            ? "#b7b183"
            : "#aabc87",
    );
    grad.addColorStop(
      1,
      ember
        ? "#8f8b66"
        : forest
          ? "#668d76"
          : r.id === "grove"
            ? "#779b75"
            : "#8da574",
    );
    shape(ctx, pts, grad as unknown as string);
    ctx.save();
    ctx.clip();
    if (this.texture.complete && this.texture.naturalWidth) {
      ctx.globalAlpha = 0.58;
      ctx.globalCompositeOperation = "multiply";
      ctx.drawImage(this.texture, r.x - r.rx, r.y - r.ry, r.rx * 2, r.ry * 2);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
    }
    for (let i = 0; i < 630; i++) {
      const x = r.x - r.rx + noise(i + r.x) * r.rx * 2,
        y = r.y - r.ry + noise(i * 3.3 + r.x) * r.ry * 2;
      ellipse(
        ctx,
        x,
        y,
        5 + noise(i * 2) * 28,
        2 + noise(i) * 9,
        i % 4 ? "#d7d79d10" : "#3b704414",
        false,
      );
    }
    // Winding paths connect actual bridge endpoints, camp and fields, with paired edging.
    ctx.lineCap = "round";
    for (const [color, width] of [
      ["#718960", 57],
      ["#b7b184", 48],
      ["#c2ba932a", 32],
    ] as const) {
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(r.x - r.rx, 485);
      ctx.bezierCurveTo(r.x - 240, 505, r.x - 180, 585, r.x, 593);
      ctx.bezierCurveTo(r.x + 210, 615, r.x + 210, 480, r.x + r.rx, 485);
      ctx.stroke();
    }
    for (let i = 0; i < 55; i++) {
      const x = r.x - r.rx + noise(i * 11 + r.x) * r.rx * 2,
        y = r.y - r.ry + noise(i * 17 + r.x) * r.ry * 2;
      if (y > 290 && y < 555 && x > r.x - 285 && x < r.x + 225) continue;
      shape(
        ctx,
        [
          [x - 4, y],
          [x - 1, y - 4],
          [x + 6, y - 2],
          [x + 4, y + 2],
        ],
        i % 2 ? "#adb398" : "#839c7d",
        false,
      );
    }
    ctx.restore();
    ctx.strokeStyle = "#d4d3a788";
    ctx.lineWidth = 3;
    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(...p) : ctx.moveTo(...p)));
    ctx.closePath();
    ctx.stroke();
    // Hanging vines remain attached to the authored coast.
    for (let i = 0; i < pts.length; i += 3) {
      const [x, y] = pts[i];
      if (y < r.y + 70) continue;
      ctx.strokeStyle = "#5d805d";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.bezierCurveTo(x + 12, y + 20, x - 10, y + 34, x + 3, y + 52);
      ctx.stroke();
      for (let k = 1; k < 5; k++)
        ellipse(ctx, x + (k % 2 ? 5 : -2), y + k * 10, 4, 2, "#91a572", false);
    }
  }

  private paintOrganicIsland(
    ctx: CanvasRenderingContext2D,
    r: (typeof REGIONS)[number],
    _theme: string,
  ) {
    // Whole authored alpha plate: grass, rock and trailing roots share one natural silhouette.
    // Register the painted bridge-mouth centres to the simulation's fixed bank positions.
    const plate = inkAsset(`island-${r.id}`)!;
    const registration =
      r.id === "grove"
        ? { half: 520, y: 358 }
        : r.id === "quarry"
          ? { half: 580, y: 387 }
          : { half: 568, y: 398 };
    const sx = 386 / registration.half,
      sy = r.ry / (r.id === "grove" ? 286 : 300);
    ctx.drawImage(
      plate,
      r.x - 768 * sx,
      r.y - registration.y * sy,
      plate.width * sx,
      plate.height * sy,
    );
  }
}
